import type { VisualizationDescriptor } from '@aibi/visualization-engine';
import type { Registry } from '@aibi/shared';
import { createContext, useContext } from 'react';
import type { HealthApi } from '../api/health-api';
import type { ReportApi } from '../report/report-api';
import type { AdminServices } from '../settings/services/admin-services';
import type { VisualizationRendererRegistry } from '../visualization/registry';
import type { AssistantPort } from '../workspace/services/assistant-port';
import type { QueryResultSource } from '../workspace/services/query-result-source';
import type { SemanticModelCatalog } from '../workspace/services/semantic-model-catalog';
import type { WorkspaceRepository } from '../workspace/services/workspace-repository';

/**
 * Servicios de infraestructura inyectados en la UI. Los componentes dependen de
 * estas interfaces, no de `fetch` ni de URLs, lo que permite probarlos aislados.
 */
export interface AppServices {
  readonly healthApi: HealthApi;
  readonly modelCatalog: SemanticModelCatalog;
  readonly queryResultSource: QueryResultSource;
  readonly assistant: AssistantPort;
  readonly workspaceRepository: WorkspaceRepository;
  /** Catalogo declarativo de tipos (requisitos y compatibilidad). */
  readonly visualizationCatalog: Registry<VisualizationDescriptor>;
  /** Renderers concretos por tipo. */
  readonly rendererRegistry: VisualizationRendererRegistry;
  /** Configuracion (requiere servidor); sin ella, la UI indica que no esta disponible. */
  readonly admin?: AdminServices;
  /** Reportes publicados (requiere servidor); publicar exige ademas `admin`. */
  readonly reports?: ReportApi;
}

export const ServicesContext = createContext<AppServices | null>(null);

export function useServices(): AppServices {
  const services = useContext(ServicesContext);
  if (services === null) {
    throw new Error('useServices debe usarse dentro de <ServicesContext value={...}>.');
  }
  return services;
}
